import { useEffect, useMemo, useState } from 'react';
import { BookPreview } from '../../components/BookPreview';
import { TokenMultiSelect } from '../../components/TokenMultiSelect';
import { BookRawJsonSchema, formatZodError, type BookRawJson } from '../../lib/bookSchema';
import { listBookSnaps, type BookSnap, type BookStatus, type UpsertBookPayload } from '../../lib/panelApi';
import { safeHttpUrl } from '../../lib/safeUrl';

export type BookEditorProps = {
  accessToken: string;
  title: string;
  /** Present only when editing an existing book — enables the snaps gallery. */
  bookUuid?: string;
  initialContent: BookRawJson;
  initialFields?: {
    status?: BookStatus;
    is_free: boolean;
    audio_cover_filename: string;
    book_cover_filename: string;
    audio_demo_filename: string;
    audio_full_filename: string;
    subtitle_full_vtt_filename?: string;
    subtitle_full_json_filename?: string;
    subtitle_demo_vtt_filename?: string;
    subtitle_demo_json_filename?: string;
    audio_full_minutes: number;
    pdf_filename?: string;
    audio_text?: string;
  };
  submitLabel: string;
  onSubmitBook: (payload: UpsertBookPayload) => Promise<void>;
  onCancel?: () => void;
};

/* ─── small helpers ─── */
function safeString(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

function safeStringArray(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x) => typeof x === 'string' && x.trim() !== '');
  if (typeof v === 'string') return v.split(',').map((s) => s.trim()).filter(Boolean);
  return [];
}

function toISBNSafe(v: unknown): string | number | undefined {
  if (typeof v === 'number') return v;
  if (typeof v === 'string') return v;
  return undefined;
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

function asNodeLabel(v: unknown): string {
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v).trim();
  if (!isPlainObject(v)) return '';
  const candidates = ['label', 'title', 'name', 'text', 'value'];
  for (const key of candidates) {
    const raw = v[key];
    if (typeof raw === 'string' && raw.trim()) return raw.trim();
    if ((typeof raw === 'number' || typeof raw === 'boolean') && String(raw).trim()) return String(raw).trim();
  }
  return '';
}

function readRawChildren(v: unknown): unknown[] {
  if (!isPlainObject(v)) return [];
  const candidates = ['children', 'nodes', 'items', 'subtopics', 'branches'];
  for (const key of candidates) {
    const raw = v[key];
    if (Array.isArray(raw)) return raw;
    if (isPlainObject(raw)) return Object.values(raw);
  }
  return [];
}

type MindMapNodeOut = { label: string; children?: MindMapNodeOut[] };
type MindMapOut = { label?: string; children?: MindMapNodeOut[] };

function normalizeMindMapNode(input: unknown): MindMapNodeOut | null {
  if (input == null) return null;
  if (Array.isArray(input)) {
    const kids = input.map(normalizeMindMapNode).filter((x): x is MindMapNodeOut => !!x);
    if (!kids.length) return null;
    return { label: 'Node', children: kids };
  }

  const label = asNodeLabel(input) || 'Node';
  const rawChildren = readRawChildren(input);
  const children = rawChildren.map(normalizeMindMapNode).filter((x): x is MindMapNodeOut => !!x);
  if (children.length > 0) return { label, children };
  return { label };
}

function normalizeMindMap(input: unknown): MindMapOut {
  if (input == null) return {};
  if (Array.isArray(input)) {
    const children = input.map(normalizeMindMapNode).filter((x): x is MindMapNodeOut => !!x);
    return children.length ? { children } : {};
  }
  if (!isPlainObject(input)) {
    const node = normalizeMindMapNode(input);
    return node ? { label: node.label, children: node.children } : {};
  }

  const label = asNodeLabel(input);
  const rawChildren = readRawChildren(input);
  const children = rawChildren.map(normalizeMindMapNode).filter((x): x is MindMapNodeOut => !!x);

  const out: MindMapOut = {};
  if (label) out.label = label;
  if (children.length) out.children = children;
  return out;
}

/** Build the unplay_assessment object, omitting empty fields/rating. */
function assembleAssessment(input: {
  difficulty_level: string;
  estimated_impact: string;
  time_to_value: string;
  best_listening_context: string;
  content_quality: number | '';
  actionability: number | '';
  timelessness: number | '';
  overall: number | '';
}): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (input.difficulty_level.trim()) out.difficulty_level = input.difficulty_level.trim();
  if (input.estimated_impact.trim()) out.estimated_impact = input.estimated_impact.trim();
  if (input.time_to_value.trim()) out.time_to_value = input.time_to_value.trim();
  if (input.best_listening_context.trim()) out.best_listening_context = input.best_listening_context.trim();

  const rating: Record<string, number> = {};
  if (input.content_quality !== '') rating.content_quality = Number(input.content_quality);
  if (input.actionability !== '') rating.actionability = Number(input.actionability);
  if (input.timelessness !== '') rating.timelessness = Number(input.timelessness);
  if (input.overall !== '') rating.overall = Number(input.overall);
  if (Object.keys(rating).length) out.unplay_rating = rating;

  return out;
}

const STATUS_COLORS: Record<BookStatus, { color: string; bg: string; label: string }> = {
  draft: { color: '#7a5c00', bg: 'rgba(234,179,8,0.18)', label: 'Draft' },
  published: { color: '#0a7a3f', bg: 'rgba(16,185,129,0.18)', label: 'Published' },
  archived: { color: '#6b7280', bg: 'rgba(107,114,128,0.18)', label: 'Archived' },
};

type TabId = 'files' | 'metadata' | 'content' | 'insights' | 'snaps';

export function BookEditor({ accessToken, title, bookUuid, initialContent, initialFields, submitLabel, onSubmitBook, onCancel }: BookEditorProps) {
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [tab, setTab] = useState<TabId>('files');
  const [showPreview, setShowPreview] = useState(false);

  /* ── publication & access ── */
  const [status, setStatus] = useState<BookStatus>(() => initialFields?.status ?? 'draft');
  const [isFree, setIsFree] = useState<boolean>(() => initialFields?.is_free ?? false);
  const [audioCoverFilename, setAudioCoverFilename] = useState<string>(() => initialFields?.audio_cover_filename ?? '');
  const [bookCoverFilename, setBookCoverFilename] = useState<string>(() => initialFields?.book_cover_filename ?? '');
  const [audioDemoFilename, setAudioDemoFilename] = useState<string>(() => initialFields?.audio_demo_filename ?? '');
  const [audioFullFilename, setAudioFullFilename] = useState<string>(() => initialFields?.audio_full_filename ?? '');
  const [audioFullMinutes, setAudioFullMinutes] = useState<number>(() => initialFields?.audio_full_minutes ?? 0);
  const [pdfFilename, setPdfFilename] = useState<string>(() => initialFields?.pdf_filename ?? '');
  const [audioText, setAudioText] = useState<string>(() => initialFields?.audio_text ?? '');

  /* ── subtitle files (produced by the pipeline; read-only R2 keys) ── */
  const [subFullVtt, setSubFullVtt] = useState<string>(() => initialFields?.subtitle_full_vtt_filename ?? '');
  const [subFullJson, setSubFullJson] = useState<string>(() => initialFields?.subtitle_full_json_filename ?? '');
  const [subDemoVtt, setSubDemoVtt] = useState<string>(() => initialFields?.subtitle_demo_vtt_filename ?? '');
  const [subDemoJson, setSubDemoJson] = useState<string>(() => initialFields?.subtitle_demo_json_filename ?? '');

  /* ── metadata ── */
  const md = initialContent.metadata ?? {};
  const [metaTitle, setMetaTitle] = useState(md.title ?? '');
  const [metaSubtitle, setMetaSubtitle] = useState(md.subtitle ?? '');
  const [metaAuthors, setMetaAuthors] = useState<string[]>(() => safeStringArray(md.authors));
  const [metaPubDate, setMetaPubDate] = useState(md.publication_date ?? '');
  const [metaISBN, setMetaISBN] = useState(String(md['ISBN-13'] ?? ''));
  const [metaPublishers, setMetaPublishers] = useState<string[]>(() => safeStringArray(md.publishers));
  const [metaPageCount, setMetaPageCount] = useState<number | ''>(md.original_page_count ?? '');
  const [metaCategory, setMetaCategory] = useState<string[]>(() => safeStringArray(md.category));
  const [metaTags, setMetaTags] = useState<string[]>(() => safeStringArray(md.tags));
  const [metaNarrator, setMetaNarrator] = useState(md.narrator_gender ?? '');
  const [metaTagline, setMetaTagline] = useState(md.short_tagline ?? '');
  const [metaDescription, setMetaDescription] = useState(md.full_description ?? '');

  /* ── legal ── */
  const lg = initialContent.legal ?? {};
  const [legalDisclaimer, setLegalDisclaimer] = useState(lg.disclaimer_text ?? '');
  const [legalCopyright, setLegalCopyright] = useState(lg.copyright_status ?? '');

  /* ── faq ── */
  const [faqItems, setFaqItems] = useState<{ question: string; answer: string }[]>(
    (initialContent.faq?.length ? initialContent.faq : [{ question: '', answer: '' }])
  );

  /* ── action plan ── */
  const [actionPlan, setActionPlan] = useState<{ day: number; task_title: string; description: string }[]>(
    (initialContent.action_plan?.length ? initialContent.action_plan : [{ day: 1, task_title: '', description: '' }])
  );

  /* ── mind map (kept as JSON textarea) ── */
  const [mindMapJson, setMindMapJson] = useState<string>(
    JSON.stringify(initialContent.mind_map ?? { label: '', children: [] }, null, 2)
  );

  /* ── glossary ── */
  const [glossary, setGlossary] = useState<{ term: string; definition: string; context: string }[]>(
    (initialContent.glossary?.length ? initialContent.glossary.map((g) => ({ term: g.term, definition: g.definition, context: g.context ?? '' })) : [{ term: '', definition: '', context: '' }])
  );

  /* ── simulation ── */
  const sim = initialContent.simulation ?? { scenario: '', challenge: '', solution: '' };
  const [simScenario, setSimScenario] = useState(sim.scenario ?? '');
  const [simChallenge, setSimChallenge] = useState(sim.challenge ?? '');
  const [simSolution, setSimSolution] = useState(sim.solution ?? '');

  /* ── social snaps ── */
  const [socialSnaps, setSocialSnaps] = useState<{ text: string }[]>(
    initialContent.social_snaps?.length
      ? initialContent.social_snaps.map((s) => (typeof s === 'string' ? { text: s } : { text: safeString((s as any)?.text) }))
      : [{ text: '' }]
  );

  /* ── key takeaways ── */
  const initialTakeaways = Array.isArray((initialContent as any).key_takeaways)
    ? ((initialContent as any).key_takeaways as any[])
    : [];
  const [keyTakeaways, setKeyTakeaways] = useState<{ title: string; insight: string }[]>(
    initialTakeaways.length
      ? initialTakeaways.map((t) => ({ title: safeString(t?.title), insight: safeString(t?.insight) }))
      : [{ title: '', insight: '' }]
  );

  /* ── who is this for ── */
  const initialWho = Array.isArray((initialContent as any).who_is_this_for)
    ? ((initialContent as any).who_is_this_for as any[]).filter((x) => typeof x === 'string')
    : [];
  const [whoIsThisFor, setWhoIsThisFor] = useState<{ text: string }[]>(
    initialWho.length ? initialWho.map((x: string) => ({ text: x })) : [{ text: '' }]
  );

  /* ── unplay assessment ── */
  const ua = ((initialContent as any).unplay_assessment ?? {}) as any;
  const uaRating = (ua?.unplay_rating ?? {}) as any;
  const [uaDifficulty, setUaDifficulty] = useState<string>(safeString(ua?.difficulty_level));
  const [uaImpact, setUaImpact] = useState<string>(safeString(ua?.estimated_impact));
  const [uaTimeToValue, setUaTimeToValue] = useState<string>(safeString(ua?.time_to_value));
  const [uaContext, setUaContext] = useState<string>(safeString(ua?.best_listening_context));
  const [uaQuality, setUaQuality] = useState<number | ''>(typeof uaRating?.content_quality === 'number' ? uaRating.content_quality : '');
  const [uaActionability, setUaActionability] = useState<number | ''>(typeof uaRating?.actionability === 'number' ? uaRating.actionability : '');
  const [uaTimelessness, setUaTimelessness] = useState<number | ''>(typeof uaRating?.timelessness === 'number' ? uaRating.timelessness : '');
  const [uaOverall, setUaOverall] = useState<number | ''>(typeof uaRating?.overall === 'number' ? uaRating.overall : '');

  const assembled = useMemo((): BookRawJson | null => {
    let mindMapObj: unknown = {};
    try {
      mindMapObj = JSON.parse(mindMapJson);
    } catch {
      mindMapObj = { label: '', children: [] };
    }
    const book: BookRawJson = {
      metadata: {
        title: metaTitle.trim(),
        subtitle: metaSubtitle.trim() || undefined,
        authors: metaAuthors.length ? metaAuthors : undefined,
        publication_date: metaPubDate.trim() || undefined,
        'ISBN-13': metaISBN.trim() ? toISBNSafe(metaISBN.trim()) : undefined,
        publishers: metaPublishers.length ? metaPublishers : undefined,
        original_page_count: metaPageCount === '' ? undefined : Number(metaPageCount),
        category: metaCategory.length ? metaCategory : undefined,
        tags: metaTags.length ? metaTags : undefined,
        narrator_gender: metaNarrator.trim() || undefined,
        short_tagline: metaTagline.trim() || undefined,
        full_description: metaDescription.trim() || undefined,
      },
      legal: {
        disclaimer_text: legalDisclaimer.trim() || undefined,
        copyright_status: legalCopyright.trim() || undefined,
      },
      faq: faqItems.filter((f) => f.question.trim() && f.answer.trim()),
      action_plan: actionPlan.filter((a) => a.task_title.trim() && a.description.trim()).map((a) => ({ ...a, day: Number(a.day) })),
      // Normalize legacy mind-map shapes (title/name/text, mixed children)
      // into the strict node schema expected by the panel validator.
      mind_map: normalizeMindMap(mindMapObj),
      glossary: glossary.filter((g) => g.term.trim() && g.definition.trim()),
      simulation: {
        scenario: simScenario.trim(),
        challenge: simChallenge.trim(),
        solution: simSolution.trim(),
      },
      social_snaps: socialSnaps.filter((s) => s.text.trim()),
      key_takeaways: keyTakeaways.filter((t) => t.title.trim() && t.insight.trim()),
      who_is_this_for: whoIsThisFor.map((w) => w.text.trim()).filter(Boolean),
      unplay_assessment: assembleAssessment({
        difficulty_level: uaDifficulty,
        estimated_impact: uaImpact,
        time_to_value: uaTimeToValue,
        best_listening_context: uaContext,
        content_quality: uaQuality,
        actionability: uaActionability,
        timelessness: uaTimelessness,
        overall: uaOverall,
      }),
    };
    return book;
  }, [metaTitle, metaSubtitle, metaAuthors, metaPubDate, metaISBN, metaPublishers, metaPageCount, metaCategory, metaTags, metaNarrator, metaTagline, metaDescription, legalDisclaimer, legalCopyright, faqItems, actionPlan, mindMapJson, glossary, simScenario, simChallenge, simSolution, socialSnaps, keyTakeaways, whoIsThisFor, uaDifficulty, uaImpact, uaTimeToValue, uaContext, uaQuality, uaActionability, uaTimelessness, uaOverall]);

  const stats = useMemo(() => {
    if (!assembled) return null;
    const desc = assembled.metadata?.full_description ?? '';
    return {
      faq: assembled.faq.length,
      actionPlan: assembled.action_plan.length,
      glossary: assembled.glossary.length,
      snaps: assembled.social_snaps.length,
      takeaways: (assembled as any).key_takeaways?.length ?? 0,
      descWordCount: desc.trim() ? desc.trim().split(/\s+/).length : 0,
    };
  }, [assembled]);

  async function onSubmit() {
    if (!assembled) return;
    const res = BookRawJsonSchema.safeParse(assembled);
    if (!res.success) {
      setValidationError(formatZodError(res.error));
      setSubmitError(null);
      setTab('metadata');
      return;
    }
    setValidationError(null);

    const missing: string[] = [];
    if (!audioCoverFilename.trim()) missing.push('audio cover');
    if (!bookCoverFilename.trim()) missing.push('book cover');
    if (!audioDemoFilename.trim()) missing.push('preview audio');
    if (!audioFullFilename.trim()) missing.push('the Unplay audio');
    if (!Number.isFinite(audioFullMinutes) || audioFullMinutes <= 0) missing.push('the Unplay duration (minutes)');
    if (missing.length) {
      setSubmitError(`Missing required file fields: ${missing.join(', ')}.`);
      setTab('files');
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      await onSubmitBook({
        status,
        is_free: isFree,
        audio_cover_filename: audioCoverFilename.trim(),
        book_cover_filename: bookCoverFilename.trim(),
        audio_demo_filename: audioDemoFilename.trim(),
        audio_full_filename: audioFullFilename.trim(),
        subtitle_full_vtt_filename: subFullVtt.trim(),
        subtitle_full_json_filename: subFullJson.trim(),
        subtitle_demo_vtt_filename: subDemoVtt.trim(),
        subtitle_demo_json_filename: subDemoJson.trim(),
        audio_full_minutes: Math.floor(audioFullMinutes),
        pdf_filename: pdfFilename.trim(),
        audio_text: audioText,
        content_json: assembled,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Request failed';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const statusMeta = STATUS_COLORS[status];

  const tabs: { id: TabId; label: string; badge?: number }[] = [
    { id: 'files', label: 'Files & Audio' },
    { id: 'metadata', label: 'Metadata' },
    { id: 'content', label: 'Content', badge: (stats?.faq ?? 0) + (stats?.actionPlan ?? 0) + (stats?.glossary ?? 0) },
    { id: 'insights', label: 'Insights', badge: (stats?.takeaways ?? 0) + (stats?.snaps ?? 0) },
  ];
  if (bookUuid) tabs.push({ id: 'snaps', label: 'Story Snaps' });

  return (
    <div>
      {/* ── Sticky action bar ── */}
      <div className="editorBar">
        <div className="editorBarLeft">
          <span className="editorTitle">{title}</span>
          <span className="statusBadge" style={{ color: statusMeta.color, background: statusMeta.bg }}>
            {statusMeta.label}
          </span>
          {stats ? (
            <span className="muted" style={{ fontSize: 12 }}>
              FAQ {stats.faq} · Action {stats.actionPlan} · Glossary {stats.glossary} · Snaps {stats.snaps} · Desc {stats.descWordCount}w
            </span>
          ) : null}
        </div>
        <div className="editorBarRight">
          <button className="btn" type="button" onClick={() => setShowPreview((v) => !v)} disabled={submitting}>
            {showPreview ? 'Hide preview' : 'Preview'}
          </button>
          {onCancel ? (
            <button className="btn" type="button" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
          ) : null}
          <button className="btn btn-primary" type="button" onClick={onSubmit} disabled={submitting}>
            {submitting ? 'Saving…' : submitLabel}
          </button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="tabs">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            className={`tab ${tab === t.id ? 'tabActive' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            {t.badge ? <span className="tabBadge">{t.badge}</span> : null}
          </button>
        ))}
      </div>

      <div className={showPreview ? 'grid' : ''}>
        <div className="card" style={{ overflow: 'visible' }}>
          {/* ── Files & Audio ── */}
          {tab === 'files' ? (
            <>
              <div className="editorSection">
                <div className="editorSectionTitle">Publication & access</div>
                <div className="fieldGrid">
                  <div className="field">
                    <div className="label">Publication status</div>
                    <select value={status} onChange={(e) => setStatus(e.target.value as BookStatus)}>
                      <option value="draft">Draft — not visible publicly</option>
                      <option value="published">Published — live on site/app</option>
                      <option value="archived">Archived — retired</option>
                    </select>
                  </div>
                  <div className="field">
                    <div className="label">Access</div>
                    <label className="checkField">
                      <input type="checkbox" checked={isFree} onChange={(e) => setIsFree(e.target.checked)} />
                      <span>Free book</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Audio & images</div>
                <div className="editorSectionHint">
                  R2 object keys (UUID filenames). Files are uploaded via the content pipeline or the dedicated
                  <strong> Uploads</strong> section — paste the resulting key here.
                </div>
                <div className="fieldGrid">
                  <Field label="Audio cover image" value={audioCoverFilename} onChange={setAudioCoverFilename} placeholder="audio_cover.jpg" />
                  <Field label="Book cover image" value={bookCoverFilename} onChange={setBookCoverFilename} placeholder="book_cover.jpg" />
                  <Field label="Preview audio (60s teaser)" value={audioDemoFilename} onChange={setAudioDemoFilename} placeholder="demo.mp3" />
                  <Field label="The Unplay (premium)" value={audioFullFilename} onChange={setAudioFullFilename} placeholder="full.mp3" />
                  <div className="field">
                    <div className="label">The Unplay duration (minutes)</div>
                    <input value={String(audioFullMinutes)} onChange={(e) => setAudioFullMinutes(Number(e.target.value))} placeholder="60" inputMode="numeric" />
                  </div>
                  <Field label="Book PDF (optional)" value={pdfFilename} onChange={setPdfFilename} placeholder="book.pdf" />
                </div>
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Subtitles (VTT / JSON)</div>
                <div className="editorSectionHint">R2 object keys uploaded by the content pipeline. Leave blank if not available.</div>
                <div className="fieldGrid">
                  <Field label="The Unplay subtitle .vtt" value={subFullVtt} onChange={setSubFullVtt} />
                  <Field label="The Unplay subtitle .json" value={subFullJson} onChange={setSubFullJson} />
                  <Field label="Preview subtitle .vtt" value={subDemoVtt} onChange={setSubDemoVtt} />
                  <Field label="Preview subtitle .json" value={subDemoJson} onChange={setSubDemoJson} />
                </div>
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Audio book text</div>
                <div className="editorSectionHint">
                  The Unplay script / transcript.
                  {audioText.trim() ? <span style={{ marginLeft: 6 }}>({audioText.trim().split(/\s+/).length} words)</span> : null}
                </div>
                <textarea className="prose" value={audioText} onChange={(e) => setAudioText(e.target.value)} placeholder="Paste the audiobook text here…" style={{ minHeight: 220 }} />
              </div>
            </>
          ) : null}

          {/* ── Metadata ── */}
          {tab === 'metadata' ? (
            <>
              <div className="editorSection">
                <div className="editorSectionTitle">Metadata</div>
                <div className="fieldGrid">
                  <Field className="full" label="Title" value={metaTitle} onChange={setMetaTitle} />
                  <Field className="full" label="Subtitle" value={metaSubtitle} onChange={setMetaSubtitle} />
                  <TokenMultiSelect
                    className="full"
                    accessToken={accessToken}
                    kind="authors"
                    label="Authors"
                    placeholder="Search or add an author…"
                    values={metaAuthors}
                    onChange={setMetaAuthors}
                  />
                  <TokenMultiSelect
                    className="full"
                    accessToken={accessToken}
                    kind="publishers"
                    label="Publishers"
                    placeholder="Search or add a publisher…"
                    values={metaPublishers}
                    onChange={setMetaPublishers}
                  />
                  <Field label="Publication date" value={metaPubDate} onChange={setMetaPubDate} />
                  <Field label="ISBN-13" value={metaISBN} onChange={setMetaISBN} />
                  <div className="field">
                    <div className="label">Original page count</div>
                    <input value={String(metaPageCount)} onChange={(e) => setMetaPageCount(e.target.value === '' ? '' : Number(e.target.value))} placeholder="291" inputMode="numeric" />
                  </div>
                  <Field label="Narrator gender" value={metaNarrator} onChange={setMetaNarrator} />
                  <TokenMultiSelect
                    className="full"
                    accessToken={accessToken}
                    kind="categories"
                    label="Categories"
                    placeholder="Search or add a category…"
                    values={metaCategory}
                    onChange={setMetaCategory}
                  />
                  <TokenMultiSelect
                    className="full"
                    accessToken={accessToken}
                    kind="tags"
                    label="Tags"
                    placeholder="Search or add a tag…"
                    values={metaTags}
                    onChange={setMetaTags}
                  />
                  <Field className="full" label="Short tagline" value={metaTagline} onChange={setMetaTagline} />
                  <div className="field full">
                    <div className="label">Full description</div>
                    <textarea className="prose" value={metaDescription} onChange={(e) => setMetaDescription(e.target.value)} placeholder="Full book description…" style={{ minHeight: 130 }} />
                  </div>
                </div>
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Legal</div>
                <div className="field">
                  <div className="label">Disclaimer text</div>
                  <textarea className="prose" value={legalDisclaimer} onChange={(e) => setLegalDisclaimer(e.target.value)} style={{ minHeight: 90 }} />
                </div>
                <div className="field">
                  <div className="label">Copyright status</div>
                  <textarea className="prose" value={legalCopyright} onChange={(e) => setLegalCopyright(e.target.value)} style={{ minHeight: 60 }} />
                </div>
              </div>
            </>
          ) : null}

          {/* ── Content ── */}
          {tab === 'content' ? (
            <>
              <div className="editorSection">
                <div className="editorSectionTitle">FAQ ({faqItems.filter((f) => f.question.trim()).length})</div>
                <ListEditor
                  items={faqItems}
                  onChange={setFaqItems}
                  addLabel="Add FAQ"
                  renderItem={(item, i, update) => (
                    <div className="subCard">
                      <Field label={`Question ${i + 1}`} value={item.question} onChange={(v) => update({ ...item, question: v })} />
                      <div className="field">
                        <div className="label">Answer</div>
                        <textarea className="prose" value={item.answer} onChange={(e) => update({ ...item, answer: e.target.value })} style={{ minHeight: 70 }} />
                      </div>
                    </div>
                  )}
                  emptyItem={{ question: '', answer: '' }}
                />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Action Plan ({actionPlan.filter((a) => a.task_title.trim()).length})</div>
                <ListEditor
                  items={actionPlan}
                  onChange={setActionPlan}
                  addLabel="Add step"
                  renderItem={(item, idx, update) => (
                    <div className="subCard">
                      <div className="field" style={{ marginTop: 0 }}>
                        <div className="label">Day</div>
                        <input type="number" min={1} value={item.day} onChange={(e) => update({ ...item, day: Number(e.target.value) })} style={{ maxWidth: 100 }} />
                      </div>
                      <Field label={`Task title ${idx + 1}`} value={item.task_title} onChange={(v) => update({ ...item, task_title: v })} />
                      <div className="field">
                        <div className="label">Description</div>
                        <textarea className="prose" value={item.description} onChange={(e) => update({ ...item, description: e.target.value })} style={{ minHeight: 70 }} />
                      </div>
                    </div>
                  )}
                  emptyItem={{ day: 1, task_title: '', description: '' }}
                />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Mind Map</div>
                <div className="editorSectionHint">Edit as JSON. Must have <code>label</code> and an optional <code>children</code> array.</div>
                <textarea value={mindMapJson} onChange={(e) => setMindMapJson(e.target.value)} spellCheck={false} style={{ minHeight: 200 }} />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Glossary ({glossary.filter((g) => g.term.trim()).length})</div>
                <ListEditor
                  items={glossary}
                  onChange={setGlossary}
                  addLabel="Add term"
                  renderItem={(item, idx, update) => (
                    <div className="subCard">
                      <Field label={`Term ${idx + 1}`} value={item.term} onChange={(v) => update({ ...item, term: v })} />
                      <div className="field">
                        <div className="label">Definition</div>
                        <textarea className="prose" value={item.definition} onChange={(e) => update({ ...item, definition: e.target.value })} style={{ minHeight: 70 }} />
                      </div>
                      <Field label="Context (optional)" value={item.context} onChange={(v) => update({ ...item, context: v })} />
                    </div>
                  )}
                  emptyItem={{ term: '', definition: '', context: '' }}
                />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Simulation</div>
                <div className="field">
                  <div className="label">Scenario</div>
                  <textarea className="prose" value={simScenario} onChange={(e) => setSimScenario(e.target.value)} style={{ minHeight: 70 }} />
                </div>
                <div className="field">
                  <div className="label">Challenge</div>
                  <textarea className="prose" value={simChallenge} onChange={(e) => setSimChallenge(e.target.value)} style={{ minHeight: 70 }} />
                </div>
                <div className="field">
                  <div className="label">Solution</div>
                  <textarea className="prose" value={simSolution} onChange={(e) => setSimSolution(e.target.value)} style={{ minHeight: 70 }} />
                </div>
              </div>
            </>
          ) : null}

          {/* ── Insights ── */}
          {tab === 'insights' ? (
            <>
              <div className="editorSection">
                <div className="editorSectionTitle">Key Takeaways ({keyTakeaways.filter((t) => t.title.trim()).length})</div>
                <ListEditor
                  items={keyTakeaways}
                  onChange={setKeyTakeaways}
                  addLabel="Add takeaway"
                  renderItem={(item, idx, update) => (
                    <div className="subCard">
                      <Field label={`Title ${idx + 1}`} value={item.title} onChange={(v) => update({ ...item, title: v })} />
                      <div className="field">
                        <div className="label">Insight</div>
                        <textarea className="prose" value={item.insight} onChange={(e) => update({ ...item, insight: e.target.value })} style={{ minHeight: 80 }} />
                      </div>
                    </div>
                  )}
                  emptyItem={{ title: '', insight: '' }}
                />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Who Is This For ({whoIsThisFor.filter((w) => w.text.trim()).length})</div>
                <ListEditor
                  items={whoIsThisFor}
                  onChange={setWhoIsThisFor}
                  addLabel="Add audience"
                  renderItem={(item, _i, update) => (
                    <div className="field" style={{ marginTop: 8 }}>
                      <textarea className="prose" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} placeholder="A reader who…" style={{ minHeight: 56 }} />
                    </div>
                  )}
                  emptyItem={{ text: '' }}
                />
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Unplay Assessment</div>
                <div className="fieldGrid">
                  <Field label="Difficulty level" value={uaDifficulty} onChange={setUaDifficulty} />
                  <Field label="Estimated impact" value={uaImpact} onChange={setUaImpact} />
                  <Field className="full" label="Best listening context" value={uaContext} onChange={setUaContext} />
                  <div className="field full">
                    <div className="label">Time to value</div>
                    <textarea className="prose" value={uaTimeToValue} onChange={(e) => setUaTimeToValue(e.target.value)} style={{ minHeight: 56 }} />
                  </div>
                </div>
                <div className="label" style={{ marginTop: 10 }}>Unplay rating (0–5)</div>
                <div className="row" style={{ gap: 10 }}>
                  <RatingInput label="Content quality" value={uaQuality} onChange={setUaQuality} />
                  <RatingInput label="Actionability" value={uaActionability} onChange={setUaActionability} />
                  <RatingInput label="Timelessness" value={uaTimelessness} onChange={setUaTimelessness} />
                  <RatingInput label="Overall" value={uaOverall} onChange={setUaOverall} />
                </div>
              </div>

              <div className="editorSection">
                <div className="editorSectionTitle">Social Snaps ({socialSnaps.filter((s) => s.text.trim()).length})</div>
                <div className="editorSectionHint">Shareable one-sentence quotes. The matching story images appear under “Story Snaps”.</div>
                <ListEditor
                  items={socialSnaps}
                  onChange={setSocialSnaps}
                  addLabel="Add snap"
                  renderItem={(item, _i, update) => (
                    <div className="field" style={{ marginTop: 8 }}>
                      <textarea className="prose" value={item.text} onChange={(e) => update({ ...item, text: e.target.value })} placeholder="One-sentence shareable quote…" style={{ minHeight: 56 }} />
                    </div>
                  )}
                  emptyItem={{ text: '' }}
                />
              </div>
            </>
          ) : null}

          {/* ── Story Snaps gallery ── */}
          {tab === 'snaps' && bookUuid ? (
            <div className="editorSection">
              <div className="editorSectionTitle">Story Snaps</div>
              <div className="editorSectionHint">Story images uploaded for this book (private bucket; links expire after 30 minutes).</div>
              <SnapGallery accessToken={accessToken} bookUuid={bookUuid} />
            </div>
          ) : null}

          {validationError ? (
            <div style={{ marginTop: 12 }}>
              <div className="sectionTitle">Validation errors</div>
              <div className="errorBox">{validationError}</div>
            </div>
          ) : null}

          {submitError ? (
            <div style={{ marginTop: 12 }}>
              <div className="sectionTitle">Request error</div>
              <div className="errorBox">{submitError}</div>
            </div>
          ) : null}
        </div>

        {showPreview ? (assembled ? <BookPreview book={assembled} /> : <PreviewPlaceholder />) : null}
      </div>
    </div>
  );
}

/* ─── shared sub-components ─── */

function Field({ label, value, onChange, className, placeholder }: { label: string; value: string; onChange: (v: string) => void; className?: string; placeholder?: string }) {
  return (
    <div className={`field ${className ?? ''}`}>
      <div className="label">{label}</div>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

function RatingInput({ label, value, onChange }: { label: string; value: number | ''; onChange: (v: number | '') => void }) {
  return (
    <div className="field" style={{ marginTop: 0, maxWidth: 150 }}>
      <div className="label">{label}</div>
      <input
        type="number"
        min={0}
        max={5}
        step={1}
        value={value === '' ? '' : String(value)}
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        placeholder="—"
        inputMode="numeric"
      />
    </div>
  );
}

function SnapGallery({ accessToken, bookUuid }: { accessToken: string; bookUuid: string }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snaps, setSnaps] = useState<BookSnap[]>([]);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await listBookSnaps(accessToken, bookUuid);
        if (!cancelled) setSnaps(res.items);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load snaps');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, bookUuid, reloadKey]);

  if (loading) return <div className="muted">Loading images…</div>;
  if (error) return <div className="errorBox">{error}</div>;
  if (!snaps.length) return <div className="muted">No story images attached to this book.</div>;

  return (
    <>
      <div className="row" style={{ marginBottom: 10 }}>
        <span className="pill">
          <span className="muted">Images</span>
          <span>{snaps.length}</span>
        </span>
        <button className="btn" type="button" onClick={() => setReloadKey((k) => k + 1)}>
          Refresh links
        </button>
      </div>
      <div className="snapGrid">
        {snaps.map((s, i) => {
          const url = safeHttpUrl(s.url);
          return (
            <a key={s.uuid} className="snapThumb" href={url || undefined} target="_blank" rel="noreferrer" title={s.filename}>
              {url ? <img src={url} alt={`Story snap ${i + 1}`} loading="lazy" /> : <div className="cap">No URL available</div>}
              <div className="cap">#{s.snap_index ?? i + 1}</div>
            </a>
          );
        })}
      </div>
    </>
  );
}

function ListEditor<T extends { [k: string]: any }>({
  items,
  onChange,
  addLabel,
  renderItem,
  emptyItem,
}: {
  items: T[];
  onChange: (items: T[]) => void;
  addLabel: string;
  renderItem: (item: T, index: number, update: (next: T) => void) => React.ReactNode;
  emptyItem: T;
}) {
  return (
    <div>
      {items.map((item, i) => (
        <div key={i}>
          {renderItem(item, i, (next) => {
            const copy = [...items];
            copy[i] = next;
            onChange(copy);
          })}
          {items.length > 1 ? (
            <div style={{ marginBottom: 8 }}>
              <button className="btn btn-danger" style={{ fontSize: 11, padding: '4px 8px' }} onClick={() => onChange(items.filter((_, idx) => idx !== i))}>
                Remove
              </button>
            </div>
          ) : null}
        </div>
      ))}
      <button className="btn" style={{ fontSize: 12 }} onClick={() => onChange([...items, emptyItem])}>
        + {addLabel}
      </button>
    </div>
  );
}

function PreviewPlaceholder() {
  return (
    <div className="card">
      <div className="sectionTitle">Preview</div>
      <div className="muted">Fill the form to see a live structured preview.</div>
    </div>
  );
}
